import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Edit3, FileDown, Files, Paperclip, Pencil, Pin, PinOff, Plus, Save, Search, StickyNote, Trash2, TrendingDown, TrendingUp, Upload, X } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";

const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 });
const CATEGORY_LABELS = { stocks: "Acciones", etfs: "ETFs", mutual_funds: "Fondos", bonds: "Bonos", crypto: "Cripto", real_estate: "Inmuebles", precious_metals: "Metales", cash: "Efectivo", foreign_currency: "Divisas", vehicles: "Vehículos", collectibles: "Coleccionables", private_businesses: "Negocios", retirement: "Retiro", other: "Otros" };
const TX_LABELS = { buy: "Compra", sell: "Venta", deposit: "Depósito", withdrawal: "Retiro", dividend: "Dividendo", interest: "Interés", staking: "Staking", airdrop: "Airdrop", split: "Split", transfer: "Transferencia", manual_adjustment: "Ajuste" };

export default function AssetDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [tab, setTab] = useState("overview");
  const [ok, setOk] = useState("");
  const [notes, setNotes] = useState([]);
  const [files, setFiles] = useState([]);

  const load = () => Promise.all([
    api.get(`/portfolio/assets/${id}`, { withCredentials: true }).then((r) => setAsset(r.data)),
    api.get(`/portfolio/meta`, { withCredentials: true }).then((r) => setMeta(r.data)),
  ]).catch((e) => setError(e.response?.data?.detail || "No pudimos cargar el activo."));
  const loadNotes = () => api.get(`/portfolio/assets/${id}/notes`, { withCredentials: true }).then((r) => setNotes(r.data || []));
  const loadFiles = () => api.get(`/portfolio/assets/${id}/files`, { withCredentials: true }).then((r) => setFiles(r.data || []));
  useEffect(() => { load(); loadNotes(); loadFiles(); /* eslint-disable-next-line */ }, [id]);

  const money2 = (v, cur = "MXN") => new Intl.NumberFormat("es-MX", { style: "currency", currency: cur === "BTC" || cur === "ETH" ? "USD" : cur, minimumFractionDigits: 2 }).format(v || 0);

  const addTx = async () => {
    try {
      const payload = { ...form };
      delete payload.id;
      ["quantity", "price", "fees", "taxes", "exchange_rate"].forEach((k) => { if (payload[k] !== "" && payload[k] !== undefined) payload[k] = Number(payload[k]); else delete payload[k]; });
      if (form.id) {
        await api.put(`/portfolio/assets/${id}/transactions/${form.id}`, payload, { withCredentials: true });
      } else {
        await api.post(`/portfolio/assets/${id}/transactions`, payload, { withCredentials: true });
      }
      setModal(null); setForm({}); await load();
    } catch (e) { setError(e.response?.data?.detail || "No pudimos guardar la transacción."); }
  };

  const editTx = (tx) => { setForm({ ...tx }); setModal("tx"); };

  const deleteTx = async (txId) => {
    if (!window.confirm("¿Eliminar esta transacción? Se recalcularán holdings.")) return;
    await api.delete(`/portfolio/assets/${id}/transactions/${txId}`, { withCredentials: true });
    await load();
  };

  const updateAsset = async (patch) => {
    setOk(""); setError("");
    try {
      await api.put(`/portfolio/assets/${id}`, patch, { withCredentials: true });
      setOk("Guardado."); await load();
    } catch (e) { setError(e.response?.data?.detail || "No pudimos guardar."); }
  };

  const deleteAsset = async () => {
    if (!window.confirm(`¿Eliminar "${asset.name}" y todas sus transacciones?`)) return;
    await api.delete(`/portfolio/assets/${id}`, { withCredentials: true });
    navigate("/patrimonio");
  };

  // ---- Notes ----
  const submitNote = async () => {
    try {
      const payload = { ...form, tags: (form.tagsRaw || "").split(",").map((t) => t.trim()).filter(Boolean) };
      delete payload.tagsRaw;
      if (form.id) {
        await api.put(`/portfolio/assets/${id}/notes/${form.id}`, payload, { withCredentials: true });
      } else {
        await api.post(`/portfolio/assets/${id}/notes`, payload, { withCredentials: true });
      }
      setModal(null); setForm({}); loadNotes();
    } catch (e) { setError(e.response?.data?.detail || "No pudimos guardar la nota."); }
  };
  const togglePinNote = async (n) => { await api.put(`/portfolio/assets/${id}/notes/${n.id}`, { pinned: !n.pinned }, { withCredentials: true }); loadNotes(); };
  const deleteNote = async (n) => {
    if (!window.confirm(`¿Eliminar la nota "${n.title}"?`)) return;
    await api.delete(`/portfolio/assets/${id}/notes/${n.id}`, { withCredentials: true }); loadNotes();
  };

  // ---- Files ----
  const uploadFiles = async (fileList, category = "other") => {
    setError(""); setOk("");
    for (const f of Array.from(fileList)) {
      const fd = new FormData();
      fd.append("file", f); fd.append("category", category); fd.append("name", f.name);
      try {
        await api.post(`/portfolio/assets/${id}/files`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      } catch (e) { setError(e.response?.data?.detail || `No pudimos subir ${f.name}.`); }
    }
    setOk("Archivos subidos."); loadFiles();
  };
  const deleteFile = async (f) => {
    if (!window.confirm(`¿Eliminar "${f.name}"?`)) return;
    await api.delete(`/portfolio/assets/${id}/files/${f.id}`, { withCredentials: true }); loadFiles();
  };

  if (!asset) return <AppShell><main className="page pd-shell"><div className="skeleton h1" /><div className="skeleton" style={{ height: 300 }} /></main></AppShell>;

  const h = asset.holdings || {};
  const isGain = (h.unrealized_gain || 0) >= 0;

  // Build cost basis timeline for chart
  const timeline = [];
  let cumQty = 0, cumCost = 0;
  const txsSorted = [...(asset.transactions || [])].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  for (const tx of txsSorted) {
    if (tx.type === "buy") { cumQty += (tx.quantity || 0); cumCost += (tx.quantity || 0) * (tx.price || 0) + (tx.fees || 0); }
    if (tx.type === "sell") { cumQty -= (tx.quantity || 0); cumCost -= (h.avg_cost || 0) * (tx.quantity || 0); }
    timeline.push({ date: tx.date, quantity: Math.round(cumQty * 100) / 100, cost: Math.round(cumCost * 100) / 100, value: Math.round(cumQty * (asset.current_price || asset.purchase_price || 0) * 100) / 100 });
  }

  return (
    <AppShell>
      <main className="page pd-shell" data-testid={`asset-detail-${id}`}>
        <header className="pd-header" style={{ "--accent": isGain ? "#10b981" : "#ef4444" }}>
          <Link to="/patrimonio" className="pd-back"><ArrowLeft size={13} /> Patrimonio</Link>
          <div className="top-row">
            <div>
              <h1 className="pd-title">{asset.name} {asset.ticker && <span style={{ color: "#94a3b8", fontWeight: 600 }}>({asset.ticker})</span>}</h1>
              <p className="pd-subtitle">{CATEGORY_LABELS[asset.category] || asset.category} · {asset.institution || "Sin institución"} · {asset.currency}</p>
            </div>
            <div className="pd-actions">
              <button onClick={() => { setForm({ type: "buy", currency: asset.currency }); setModal("tx"); }} className="cash-btn primary" data-testid="add-tx-btn"><Plus size={14} /> Nueva transacción</button>
              <button onClick={deleteAsset} className="cash-btn ghost" style={{ color: "#ef4444" }} data-testid="delete-asset-btn"><Trash2 size={14} /> Eliminar</button>
            </div>
          </div>
          <div className="pd-header-strip">
            <div className="item"><small>Cantidad</small><b>{h.quantity}</b></div>
            <div className="item"><small>Costo promedio</small><b>{money2(h.avg_cost, asset.currency)}</b></div>
            <div className="item"><small>Precio actual</small><b>{money2(h.current_price, asset.currency)}</b></div>
            <div className="item"><small>Valor total</small><b>{money2(h.current_value, asset.currency)}</b></div>
            <div className="item"><small>G/L no realizada</small><b style={{ color: isGain ? "#10b981" : "#ef4444" }}>{isGain ? "+" : ""}{money2(h.unrealized_gain, asset.currency)}</b></div>
            <div className="item"><small>% Rendimiento</small><b style={{ color: isGain ? "#10b981" : "#ef4444" }}>{h.pct_return}%</b></div>
          </div>
        </header>

        <nav className="pd-tabs" data-testid="asset-tabs">
          <button className={tab === "overview" ? "active" : ""} onClick={() => setTab("overview")} data-testid="asset-tab-overview">Resumen</button>
          <button className={tab === "transactions" ? "active" : ""} onClick={() => setTab("transactions")} data-testid="asset-tab-transactions">Transacciones ({asset.transactions?.length || 0})</button>
          <button className={tab === "notes" ? "active" : ""} onClick={() => setTab("notes")} data-testid="asset-tab-notes"><StickyNote size={13} /> Notas ({notes.length})</button>
          <button className={tab === "files" ? "active" : ""} onClick={() => setTab("files")} data-testid="asset-tab-files"><Paperclip size={13} /> Archivos ({files.length})</button>
          <button className={tab === "settings" ? "active" : ""} onClick={() => setTab("settings")} data-testid="asset-tab-settings">Ajustes</button>
        </nav>

        {error && <p className="expenses-error" data-testid="asset-error">{error}</p>}
        {ok && <p style={{ background: "#dcfce7", color: "#166534", padding: 10, borderRadius: 10 }}>{ok}</p>}

        {tab === "overview" && (
          <section data-testid="asset-overview">
            <div className="pd-kpi-grid">
              <div className="pd-kpi contract"><small>Costo total</small><b>{money2(h.cost_basis, asset.currency)}</b></div>
              <div className="pd-kpi collected"><small>Valor MXN</small><b>{money.format(asset.value_mxn || 0)}</b><small className="delta">{asset.allocation_pct}% de tu portfolio</small></div>
              <div className={`pd-kpi ${isGain ? "collected" : "expense"}`}><small>G/L no realizada</small><b style={{ color: isGain ? "#10b981" : "#ef4444" }}>{isGain ? "+" : ""}{money2(h.unrealized_gain, asset.currency)}</b></div>
              <div className={`pd-kpi ${(h.realized_gain || 0) >= 0 ? "collected" : "expense"}`}><small>G/L realizada</small><b style={{ color: (h.realized_gain || 0) >= 0 ? "#10b981" : "#ef4444" }}>{money2(h.realized_gain, asset.currency)}</b></div>
              <div className="pd-kpi income"><small>Ingresos (div/int/staking)</small><b>{money2(h.income, asset.currency)}</b></div>
              <div className="pd-kpi profit"><small>Storage</small><b style={{ fontSize: 15 }}>{asset.storage_location || "—"}</b></div>
            </div>

            {timeline.length >= 2 && (
              <div style={{ background: "#fff", borderRadius: 18, padding: 22, border: "1px solid #e2e8f0" }} data-testid="asset-chart">
                <h3 style={{ margin: "0 0 14px", font: "900 16px Nunito, sans-serif" }}>Evolución de tu posición</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={timeline}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" style={{ fontSize: 11 }} />
                    <YAxis style={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="cost" name="Costo" stroke="#ef4444" strokeWidth={2} />
                    <Line type="monotone" dataKey="value" name="Valor" stroke="#10b981" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {asset.notes && (
              <div style={{ background: "#fff", borderRadius: 18, padding: 22, border: "1px solid #e2e8f0", marginTop: 20 }}>
                <h3 style={{ margin: "0 0 8px", font: "900 15px Nunito, sans-serif" }}>Notas</h3>
                <p style={{ color: "#475569", whiteSpace: "pre-wrap" }}>{asset.notes}</p>
              </div>
            )}
          </section>
        )}

        {tab === "transactions" && (
          <section data-testid="asset-transactions">
            <div style={{ background: "#fff", borderRadius: 18, border: "1px solid #e2e8f0", overflow: "hidden" }}>
              {(asset.transactions || []).length === 0 ? (
                <div className="pd-empty" data-testid="tx-empty" style={{ margin: 0, border: "none" }}>
                  <div className="pd-empty-icon"><Plus size={22} /></div>
                  <h3>Aún sin transacciones.</h3>
                  <p>Registra compras, ventas, dividendos y más para calcular tu rendimiento real.</p>
                  <button onClick={() => { setForm({ type: "buy", currency: asset.currency }); setModal("tx"); }} className="cash-btn primary"><Plus size={14} /> Nueva</button>
                </div>
              ) : (
                <table className="expenses-table" style={{ margin: 0 }} data-testid="tx-table">
                  <thead>
                    <tr><th>Fecha</th><th>Tipo</th><th style={{ textAlign: "right" }}>Cantidad</th><th style={{ textAlign: "right" }}>Precio</th><th style={{ textAlign: "right" }}>Fees</th><th style={{ textAlign: "right" }}>Total</th><th></th></tr>
                  </thead>
                  <tbody>
                    {(asset.transactions || []).map((tx) => {
                      const total = (tx.quantity || 0) * (tx.price || 0) + (tx.fees || 0);
                      const color = tx.type === "buy" || tx.type === "deposit" ? "#ef4444" : (tx.type === "sell" || tx.type === "withdrawal") ? "#10b981" : "#4169e1";
                      return (
                        <tr key={tx.id} data-testid={`tx-row-${tx.id}`}>
                          <td>{tx.date}</td>
                          <td><span style={{ color, fontWeight: 700 }}>{TX_LABELS[tx.type] || tx.type}</span></td>
                          <td style={{ textAlign: "right" }}>{tx.quantity || "—"}</td>
                          <td style={{ textAlign: "right" }}>{tx.price ? money2(tx.price, tx.currency) : "—"}</td>
                          <td style={{ textAlign: "right", color: "#94a3b8" }}>{tx.fees ? money2(tx.fees, tx.currency) : "—"}</td>
                          <td style={{ textAlign: "right", fontWeight: 800 }}>{total ? money2(total, tx.currency) : "—"}</td>
                          <td><button onClick={() => editTx(tx)} className="cash-btn ghost" style={{ padding: "4px 8px", color: "#4169e1", marginRight: 4 }} data-testid={`tx-edit-${tx.id}`}><Edit3 size={13} /></button><button onClick={() => deleteTx(tx.id)} className="cash-btn ghost" style={{ padding: "4px 8px", color: "#ef4444" }} data-testid={`tx-delete-${tx.id}`}><Trash2 size={13} /></button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        )}

        {tab === "notes" && (
          <AssetNotesTab notes={notes}
            onAdd={() => { setForm({}); setModal("note"); }}
            onEdit={(n) => { setForm({ ...n, tagsRaw: (n.tags || []).join(", ") }); setModal("note"); }}
            onTogglePin={togglePinNote}
            onDelete={deleteNote}
          />
        )}

        {tab === "files" && (
          <AssetFilesTab files={files} assetId={id} onUpload={uploadFiles} onDelete={deleteFile} />
        )}

        {tab === "settings" && (
          <AssetSettings asset={asset} meta={meta} onSave={updateAsset} />
        )}

        {modal === "tx" && <TxModal form={form} setForm={setForm} meta={meta} defaultCurrency={asset.currency} onClose={() => setModal(null)} onSubmit={addTx} />}
        {modal === "note" && <AssetNoteModal form={form} setForm={setForm} onClose={() => setModal(null)} onSubmit={submitNote} />}
      </main>
    </AppShell>
  );
}

function AssetSettings({ asset, meta, onSave }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    setForm({
      name: asset.name || "", ticker: asset.ticker || "", category: asset.category || "",
      institution: asset.institution || "", storage_location: asset.storage_location || "",
      currency: asset.currency || "MXN", current_price: asset.current_price ?? "",
      status: asset.status || "active", notes: asset.notes || "",
    });
  }, [asset]);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = () => {
    const payload = { ...form };
    if (payload.current_price === "") delete payload.current_price;
    else payload.current_price = Number(payload.current_price);
    onSave(payload);
  };

  return (
    <section data-testid="tab-settings" style={{ background: "#fff", borderRadius: 18, padding: 22, border: "1px solid #e2e8f0" }}>
      <h3 style={{ margin: "0 0 14px", font: "900 16px Nunito, sans-serif" }}>Ajustes del activo</h3>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <label>Nombre<input value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="settings-asset-name" /></label>
        <label>Ticker<input value={form.ticker || ""} onChange={(e) => set("ticker", e.target.value.toUpperCase())} /></label>
        <label>Categoría
          <select value={form.category || ""} onChange={(e) => set("category", e.target.value)}>
            {(meta?.categories || []).map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
          </select>
        </label>
        <label>Institución<input value={form.institution || ""} onChange={(e) => set("institution", e.target.value)} /></label>
        <label>Storage
          <select value={form.storage_location || ""} onChange={(e) => set("storage_location", e.target.value)}>
            <option value="">—</option>
            {(meta?.storage_locations || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>Moneda
          <select value={form.currency || "MXN"} onChange={(e) => set("currency", e.target.value)}>
            {["MXN", "USD", "EUR", "GBP", "CAD", "JPY", "BTC", "ETH"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label>Precio actual<input type="number" step="0.01" value={form.current_price ?? ""} onChange={(e) => set("current_price", e.target.value)} data-testid="settings-current-price" /></label>
        <label>Estatus
          <select value={form.status || "active"} onChange={(e) => set("status", e.target.value)} data-testid="settings-asset-status">
            <option value="active">Activo</option>
            <option value="sold">Vendido</option>
            <option value="closed">Cerrado</option>
            <option value="archived">Archivado</option>
          </select>
        </label>
      </div>
      <label>Notas<textarea rows={3} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></label>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
        <button onClick={save} className="cash-btn primary" data-testid="settings-asset-save"><Save size={14} /> Guardar</button>
      </div>
    </section>
  );
}

function TxModal({ form, setForm, meta, defaultCurrency, onClose, onSubmit }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="cash-modal-backdrop" onClick={onClose} data-testid="tx-modal">
      <div className="cash-modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(600px, 96vw)" }}>
        <header><div><small>{form.id ? "EDITAR TRANSACCIÓN" : "NUEVA TRANSACCIÓN"}</small><b>Se recalculan holdings al guardar</b></div><button onClick={onClose}><X size={18} /></button></header>
        <div style={{ padding: "20px 24px 8px" }}>
          <div className="cash-modal-grid">
            <label>Tipo *
              <select value={form.type || "buy"} onChange={(e) => set("type", e.target.value)} data-testid="tx-type">
                {(meta?.tx_types || []).map((t) => <option key={t} value={t}>{TX_LABELS[t] || t}</option>)}
              </select>
            </label>
            <label>Fecha<input type="date" value={form.date || ""} onChange={(e) => set("date", e.target.value)} data-testid="tx-date" /></label>
            <label>Cantidad<input type="number" step="any" value={form.quantity || ""} onChange={(e) => set("quantity", e.target.value)} placeholder="Ej: 10" data-testid="tx-quantity" /></label>
            <label>Precio unitario<input type="number" step="0.01" value={form.price || ""} onChange={(e) => set("price", e.target.value)} placeholder="Ej: 150.00" data-testid="tx-price" /></label>
            <label>Fees<input type="number" step="0.01" value={form.fees || ""} onChange={(e) => set("fees", e.target.value)} placeholder="Ej: 5.00" /></label>
            <label>Impuestos<input type="number" step="0.01" value={form.taxes || ""} onChange={(e) => set("taxes", e.target.value)} placeholder="0" /></label>
            <label>Moneda
              <select value={form.currency || defaultCurrency} onChange={(e) => set("currency", e.target.value)}>
                {["MXN", "USD", "EUR", "GBP", "CAD", "JPY", "BTC", "ETH"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="full">Notas<textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="Ej: Vendí porque llegué a mi target" /></label>
          </div>
        </div>
        <div className="cash-modal-actions">
          <button onClick={onClose} className="cash-btn ghost">Cancelar</button>
          <button onClick={onSubmit} className="cash-btn primary" disabled={!form.type} data-testid="tx-save">{form.id ? "Guardar cambios" : "Guardar transacción"}</button>
        </div>
      </div>
    </div>
  );
}

const FILE_CATS_PORTFOLIO = [
  { key: "statement", label: "Estado de cuenta" },
  { key: "contract", label: "Contrato" },
  { key: "purchase", label: "Compra" },
  { key: "receipt", label: "Recibo" },
  { key: "insurance", label: "Seguro" },
  { key: "tax", label: "Fiscal" },
  { key: "appraisal", label: "Avalúo" },
  { key: "ownership", label: "Propiedad" },
  { key: "wallet_backup", label: "Backup wallet" },
  { key: "other", label: "Otro" },
];

const bytesTo2 = (b) => {
  if (!b) return "0 B"; const u=["B","KB","MB","GB"]; let i=0, n=b;
  while (n>=1024 && i<u.length-1) { n/=1024; i++; }
  return `${n.toFixed(n<10&&i>0?1:0)} ${u[i]}`;
};

function AssetNotesTab({ notes, onAdd, onEdit, onTogglePin, onDelete }) {
  const [search, setSearch] = useState("");
  const filtered = notes.filter((n) => !search || (n.title + " " + (n.description || "") + " " + (n.tags || []).join(" ")).toLowerCase().includes(search.toLowerCase()));
  return (
    <section data-testid="asset-notes-tab">
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <div className="projects-toolbar" style={{ flex: 1, margin: 0 }}>
          <div className="search" style={{ flex: 1 }}>
            <Search size={16} color="#94a3b8" />
            <input placeholder="Buscar en tus notas…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="asset-notes-search" />
          </div>
        </div>
        <button onClick={onAdd} className="cash-btn primary" data-testid="asset-notes-add"><Plus size={14} /> Nueva</button>
      </div>
      {filtered.length === 0 ? (
        <div className="pd-empty" data-testid="asset-notes-empty">
          <div className="pd-empty-icon"><StickyNote size={22} /></div>
          <h3>Sin notas todavía.</h3>
          <p>Guarda tu tesis de inversión, plan de salida y observaciones del mercado.</p>
          <button onClick={onAdd} className="cash-btn primary"><Plus size={14} /> Crear la primera</button>
        </div>
      ) : (
        <div className="notes-list">
          {filtered.map((n) => (
            <article key={n.id} className={`note-card ${n.pinned ? "pinned" : ""}`} data-testid={`asset-note-${n.id}`}>
              <div className="note-head">
                <h3>{n.pinned && <Pin size={14} color="#f59e0b" />} {n.title}</h3>
                <div className="note-actions">
                  <button onClick={() => onTogglePin(n)} title={n.pinned ? "Desfijar" : "Fijar"}>{n.pinned ? <PinOff size={14} /> : <Pin size={14} />}</button>
                  <button onClick={() => onEdit(n)} title="Editar"><Pencil size={14} /></button>
                  <button onClick={() => onDelete(n)} className="del" title="Eliminar"><Trash2 size={14} /></button>
                </div>
              </div>
              {n.description && <p>{n.description}</p>}
              <div className="note-meta">
                <small>{n.date}</small>
                {(n.tags || []).map((t) => <span key={t} className="note-tag">{t}</span>)}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function AssetFilesTab({ files, assetId, onUpload, onDelete }) {
  const [dragging, setDragging] = useState(false);
  const [pickCat, setPickCat] = useState("statement");
  const ref = useRef(null);
  const onDrop = (e) => { e.preventDefault(); setDragging(false); if (e.dataTransfer?.files?.length) onUpload(e.dataTransfer.files, pickCat); };
  const isImg = (ext) => ["png","jpg","jpeg","gif","webp","heic","heif"].includes((ext||"").toLowerCase());

  return (
    <section data-testid="asset-files-tab">
      <div style={{ display: "flex", gap: 12, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
        <select value={pickCat} onChange={(e) => setPickCat(e.target.value)} data-testid="asset-file-cat" style={{ padding: "8px 12px", borderRadius: 10, border: "1px solid #e2e8f0", font: "600 13px Nunito, sans-serif" }}>
          {FILE_CATS_PORTFOLIO.map((c) => <option key={c.key} value={c.key}>Subir como: {c.label}</option>)}
        </select>
      </div>
      <div
        className={`files-drop ${dragging ? "dragging" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => ref.current?.click()}
        data-testid="asset-files-dropzone"
      >
        <Upload size={26} style={{ marginBottom: 6 }} />
        <div style={{ font: "800 15px Nunito, sans-serif" }}>Arrastra archivos o haz click</div>
        <small>Estados de cuenta, contratos, avalúos, backup wallets · hasta 25 MB</small>
        <input ref={ref} type="file" multiple onChange={(e) => e.target.files && onUpload(e.target.files, pickCat)} style={{ display: "none" }} data-testid="asset-files-input" />
      </div>
      {files.length === 0 ? (
        <div className="pd-empty" style={{ marginTop: 20 }} data-testid="asset-files-empty">
          <div className="pd-empty-icon"><Paperclip size={22} /></div>
          <h3>Sube tu primer documento.</h3>
          <p>Estados de cuenta, contratos, avalúos, backup de wallets, todo en un lugar.</p>
        </div>
      ) : (
        <div className="files-grid">
          {files.map((f) => (
            <article key={f.id} className="file-card" data-testid={`asset-file-${f.id}`}>
              <span className="file-cat">{(FILE_CATS_PORTFOLIO.find(c => c.key === f.category) || {label:"Otro"}).label}</span>
              <div className="file-preview">
                {isImg(f.extension)
                  ? <img src={`${api.defaults.baseURL}/portfolio/assets/${assetId}/files/${f.id}/download`} alt={f.name} />
                  : <FileDown size={36} />}
              </div>
              <h4>{f.name}</h4>
              <small>{bytesTo2(f.size)} · {new Date(f.created_at).toLocaleDateString("es-MX")}</small>
              <div className="file-actions">
                <a href={`${api.defaults.baseURL}/portfolio/assets/${assetId}/files/${f.id}/download`} target="_blank" rel="noreferrer"><FileDown size={12} /> Descargar</a>
                <button className="del" onClick={() => onDelete(f)}><Trash2 size={12} /></button>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function AssetNoteModal({ form, setForm, onClose, onSubmit }) {
  return (
    <div className="cash-modal-backdrop" onClick={onClose} data-testid="asset-note-modal">
      <div className="cash-modal" onClick={(e) => e.stopPropagation()}>
        <header><div><small>{form.id ? "EDITAR NOTA" : "NUEVA NOTA"}</small><b>Del activo</b></div><button onClick={onClose}><X size={18} /></button></header>
        <div style={{ padding: "0 24px 16px" }}>
          <label>Título<input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="asset-note-title" /></label>
          <label>Descripción<textarea rows={4} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="asset-note-description" /></label>
          <label>Tags (separadas por coma)<input value={form.tagsRaw || ""} onChange={(e) => setForm({ ...form, tagsRaw: e.target.value })} placeholder="tesis, largo-plazo, cripto" /></label>
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontWeight: 700 }}>
            <input type="checkbox" checked={!!form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} /> Fijar arriba
          </label>
        </div>
        <div className="cash-modal-actions">
          <button onClick={onClose} className="cash-btn ghost">Cancelar</button>
          <button onClick={onSubmit} className="cash-btn primary" disabled={!form.title} data-testid="asset-note-save">Guardar</button>
        </div>
      </div>
    </div>
  );
}
